import { Footer } from 'components/footer/footer';
import { Jumbotron } from 'components/Shared/Jumbotron/Jumbotron';
import { Col, ContentSet, Row, Section } from 'components/Shared/Scaffolding';
import React, { useCallback } from 'react';
import styles from './plugins-wordpress.module.scss';
import screenshot from '../assets/images/screenshot-plugin.png';
import { GLYPHS, Icon } from 'components/Shared/Icon';
import classNames from 'classnames';
import { checkout, WORDPRESS_MULTI_ID, WORDPRESS_SINGLE_ID } from 'utils/payments';
import { useDispatch, useSelector } from 'react-redux';
import { currentUserAuthenticatedSelector } from 'modules/currentUser';
import MODAL from 'components/Shared/Modals';
import { showModal } from 'modules/modal';
import { useMetaTags } from 'hooks/useMetaTags';

const PAGE_DESCRIPTION =
    'The Snazzy Maps WordPress plugin adds styles to your existing Google Maps with the click of a button.';
const PAGE_KEYWORDS = 'snazzy maps,wordpress,google maps,plugin,styles';

/**
 * Page for viewing and purchasing the WordPress plugin.
 */
export default function PluginsWordPress() {
    useMetaTags(PAGE_DESCRIPTION, PAGE_KEYWORDS);
    return (
        <>
            <Jumbotron className={styles.plugins_jumbotron}>
                <ContentSet className={styles.container}>
                    <header>
                        <span className="h3 no-margin text-primary">Snazzy Maps</span>
                        <h1 className="no-margin">WordPress Plugin</h1>
                    </header>
                    <p className="lead">Apply styles to Google Maps in WordPress with the click of a button.</p>
                    <a
                        href="https://snazzymaps.com/plugins/download/wordpress/snazzy-maps.1.4.0.zip"
                        className="btn btn-primary btn-lg btn-drop-shadow btn-border-radius"
                    >
                        Download
                    </a>
                    <div>
                        <em>Free for personal use. Affordable for businesses.</em>
                    </div>
                </ContentSet>
            </Jumbotron>
            <Section type="sub">
                <ContentSet className="container-fluid container-max-width content-padded-y-lg">
                    <Row>
                        <Col span="col-md-8 col-md-offset-2">
                            <img src={screenshot} className={styles.screenshot} alt="Preview of our WordPress plugin" />
                        </Col>
                    </Row>
                </ContentSet>
            </Section>
            <Section>
                <ContentSet className="container-fluid container-max-width content-padded-y-lg">
                    <Row>
                        <Col span="col-lg-10 col-lg-offset-1 content-set">
                            <h1 className="text-center no-margin">Features</h1>
                            <div className="row-spaced-wrapper">
                                <div className="row flex flex-wrap">
                                    <section className="plugin-feature col-md-4 flex-item-grow-shrink flex">
                                        <div className="bordered-content content-padded content-set flex-item-grow-shrink">
                                            <div className="text-center">
                                                <Icon glyph={GLYPHS.SNAZZY_MAPS} className={styles.feature_icon} />
                                            </div>
                                            <h1 className="h3 title text-center">Quickly apply styles</h1>
                                            <p>
                                                Browse through hundreds of styles and choose the one that's right for
                                                you. Quickly apply your favorite style to all the maps on your WordPress
                                                site.
                                            </p>
                                        </div>
                                    </section>
                                    <section className="plugin-feature col-md-4 flex-item-grow-shrink flex">
                                        <div className="bordered-content content-padded content-set flex-item-grow-shrink">
                                            <div className="text-center">
                                                <Icon glyph={GLYPHS.GOOGLE} className={styles.feature_icon} />
                                            </div>
                                            <h1 className="h3 title text-center">Works with other map plugins</h1>
                                            <p>
                                                The Snazzy Maps WordPress plugin works alongside your existing Google
                                                Maps plugins to make your maps just a little bit snazzier.
                                            </p>
                                        </div>
                                    </section>
                                    <section className="plugin-feature col-md-4 flex-item-grow-shrink flex">
                                        <div className="bordered-content content-padded content-set flex-item-grow-shrink">
                                            <div className="text-center">
                                                <Icon glyph={GLYPHS.STAR} className={styles.feature_icon} />
                                            </div>
                                            <h1 className="h3 title text-center">Access your favorites</h1>
                                            <p>
                                                Add in your account key and access your list of favorites to apply
                                                styles even faster. You can also access private styles that you have
                                                created in our editor.
                                            </p>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </ContentSet>
            </Section>
            <Section type="sub">
                <ContentSet className="container-fluid container-max-width content-padded-y-lg">
                    <Row>
                        <Col span="col-lg-10 col-lg-offset-1 content-set">
                            <h1 className="text-center no-margin">Pricing</h1>
                            <div className="row-spaced-wrapper">
                                <div className="row flex flex-wrap">
                                    <section
                                        className={classNames(
                                            styles.pricing_tier,
                                            'col-md-4 flex-item-grow-shrink flex'
                                        )}
                                    >
                                        <div className="bordered-content flex-item-grow-shrink flex flex-dir-col">
                                            <div className={styles.price}>
                                                <h1 className="h4 text-primary no-margin">
                                                    Open source and/or personal use
                                                </h1>
                                                <div className={styles.subtitle}>FREE</div>
                                            </div>
                                            <div className={classNames(styles.info, 'flex-item-grow-shrink')}>
                                                <p>
                                                    Download and use the plugin free of charge on open source projects
                                                    or on your personal site.
                                                </p>
                                            </div>
                                            <div className={styles.action}>
                                                <a
                                                    className="btn btn-primary btn-drop-shadow btn-border-radius"
                                                    href="https://snazzymaps.com/plugins/download/wordpress/snazzy-maps.1.4.0.zip"
                                                >
                                                    Download
                                                </a>
                                            </div>
                                        </div>
                                    </section>
                                    <section
                                        className={classNames(
                                            styles.pricing_tier,
                                            'col-md-4 flex-item-grow-shrink flex'
                                        )}
                                    >
                                        <div className="bordered-content flex-item-grow-shrink flex flex-dir-col">
                                            <div className={styles.price}>
                                                <h1 className="h4 text-primary no-margin">
                                                    Business - Single Site License
                                                </h1>
                                                <div className={styles.subtitle}>
                                                    <span>$10.00</span>
                                                </div>
                                            </div>
                                            <div className={classNames(styles.info, 'flex-item-grow-shrink')}>
                                                <p>
                                                    Purchase a license to use the plugin on a single business related
                                                    site. Helps support future development on the site and even more
                                                    plugins.
                                                </p>
                                            </div>
                                            <div className={styles.action}>
                                                <PurchaseButton itemId={WORDPRESS_SINGLE_ID} />
                                            </div>
                                        </div>
                                    </section>
                                    <section
                                        className={classNames(
                                            styles.pricing_tier,
                                            'col-md-4 flex-item-grow-shrink flex'
                                        )}
                                    >
                                        <div className="bordered-content flex-item-grow-shrink flex flex-dir-col">
                                            <div className={styles.price}>
                                                <h1 className="h4 text-primary no-margin">
                                                    Business - Multiple Site License
                                                </h1>
                                                <div className={styles.subtitle}>
                                                    <span>$20.00</span>
                                                </div>
                                            </div>
                                            <div className={classNames(styles.info, 'flex-item-grow-shrink')}>
                                                <p>
                                                    Use the plugin on as many sites as you want and support future
                                                    development on the site.
                                                </p>
                                            </div>
                                            <div className={styles.action}>
                                                <PurchaseButton itemId={WORDPRESS_MULTI_ID} />
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </ContentSet>
            </Section>
            <Section type="dark" className="text-center">
                <div className="container-fluid container-max-width content-padded-y-lg content-set">
                    <h1 className="h2 no-margin">Visit our plugin page on WordPress.org for more details</h1>
                    <a
                        href="https://wordpress.org/plugins/snazzy-maps/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-lg btn-drop-shadow btn-border-radius btn-text-wrap"
                    >
                        View Details
                    </a>
                </div>
            </Section>
            <Footer />
        </>
    );
}

const PurchaseButton = ({ itemId }) => {
    const authenticated = useSelector(currentUserAuthenticatedSelector);
    const dispatch = useDispatch();
    const showAuthModal = useCallback(() => dispatch(showModal(MODAL.NOT_AUTHENTICATED)), [dispatch]);
    const checkoutItem = useCallback(() => checkout(itemId), [itemId]);
    const onClick = authenticated ? checkoutItem : showAuthModal;

    return (
        <button className="btn btn-primary btn-drop-shadow btn-border-radius" type="button" onClick={onClick}>
            Purchase
        </button>
    );
};
